/**
 * Started / mastered / total counts for a library cookbook (user deck cards + SRS progress).
 * Uses the same rules as RoadmapList topic rows (`isSnippetStarted` / `isSnippetMastered`).
 */

import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { resolveSrsNextReviewDate } from '../components/SRS/algorithm';
import { isSnippetMastered, isSnippetStarted } from '../components/Roadmap/RoadmapList';
import { isCookbookSnippetDueToday } from './studySessionDotState';
import { snippetMasteredFromProgress } from '../services/profileAchievementStatsService';
import { MAX_SNIPPET_LEVEL } from '../components/SRS/LevelingSystem';

/**
 * @param {Array<{ id?: string, deckId?: string, cardId?: string }>} progressRows
 * @returns {Map<string, Record<string, unknown>>}
 */
export function buildCardProgressMap(progressRows) {
  /** @type {Map<string, Record<string, unknown>>} */
  const map = new Map();
  if (!Array.isArray(progressRows)) return map;
  for (const row of progressRows) {
    if (!row || typeof row !== 'object') continue;
    if (row.deckId && row.cardId) {
      map.set(`${row.deckId}_${row.cardId}`, row);
    } else if (typeof row.id === 'string' && row.id.includes('_')) {
      map.set(row.id, row);
    }
  }
  return map;
}

/**
 * Whether an SRS progress row belongs to a cookbook card document.
 * Matches Firestore card id or legacy roadmapSnippetId keys stored in progress.cardId.
 *
 * @param {{ id?: string, roadmapSnippetId?: string }} card
 * @param {{ cardId?: string }} progressRow
 */
export function cardMatchesProgressRow(card, progressRow) {
  if (!card || !progressRow?.cardId) return false;
  const progressCardId = String(progressRow.cardId);
  if (String(card.id) === progressCardId) return true;
  const snippetId =
    card.roadmapSnippetId != null ? String(card.roadmapSnippetId).trim() : '';
  return snippetId !== '' && snippetId === progressCardId;
}

/**
 * @param {{ id?: string, roadmapSnippetId?: string }} card
 * @param {Array<{ deckId?: string, cardId?: string }>} progressRows
 */
export function findProgressForCard(card, progressRows) {
  if (!card || !Array.isArray(progressRows)) return null;
  return progressRows.find((row) => cardMatchesProgressRow(card, row)) || null;
}

/**
 * Resolve SRS progress for a deck card using id and roadmapSnippetId aliases.
 *
 * @param {string} deckId
 * @param {{ id: string } & Record<string, unknown>} cardData
 * @param {Map<string, Record<string, unknown>>} progressMap
 * @param {Array<{ deckId?: string, cardId?: string }>} [progressRows]
 */
export function resolveProgressForDeckCard(deckId, cardData, progressMap, progressRows = []) {
  const cardId = cardData?.id;
  if (!deckId || !cardId) return null;

  const byDocId = progressMap.get(`${deckId}_${cardId}`);
  if (byDocId) return byDocId;

  const snippetId =
    cardData.roadmapSnippetId != null ? String(cardData.roadmapSnippetId).trim() : '';
  if (snippetId) {
    const bySnippet = progressMap.get(`${deckId}_${snippetId}`);
    if (bySnippet) return bySnippet;
  }

  return findProgressForCard({ id: cardId, ...cardData }, progressRows);
}

/**
 * First unstarted card after `currentIndex`, wrapping to the start of the deck.
 * @param {Array<{ id?: string, progress?: Record<string, unknown> | null }>} cards
 * @param {number} currentIndex
 * @returns {number} index or -1 when every snippet has been started
 */
export function findNextUnstartedCookbookCardIndex(cards, currentIndex) {
  if (!Array.isArray(cards) || cards.length === 0) return -1;

  const from = Number.isFinite(currentIndex) ? currentIndex : -1;
  const isUnstarted = (card) =>
    !isSnippetStarted(statsFromCookbookCard(card, card?.progress));

  for (let i = from + 1; i < cards.length; i += 1) {
    if (isUnstarted(cards[i])) return i;
  }
  for (let i = 0; i < from; i += 1) {
    if (isUnstarted(cards[i])) return i;
  }
  return -1;
}

/**
 * Effective SRS due time for ordering cookbook snippets (unstarted = now).
 * @param {{ progress?: Record<string, unknown> | null } | null | undefined} card
 * @param {Date} [now]
 * @returns {Date}
 */
export function resolveCookbookCardReviewDueAt(card, now = new Date()) {
  const progress = card?.progress;
  const stats = statsFromCookbookCard(card, progress);
  if (!isSnippetStarted(stats)) {
    return new Date(now);
  }
  const due = resolveSrsNextReviewDate(progress, now);
  return due || new Date(now);
}

/**
 * Whether a cookbook card should be auto-advanced to after another snippet completes.
 * @param {{ progress?: Record<string, unknown> | null } | null | undefined} card
 * @param {Date} [now]
 */
export function isCookbookCardEligibleForAutoAdvance(card, now = new Date()) {
  const stats = statsFromCookbookCard(card, card?.progress);
  if (!isSnippetStarted(stats)) return true;
  return isCookbookSnippetDueToday(card?.progress, now);
}

/**
 * Pick a random card index for practice sessions, excluding the current card.
 * @param {Array<{ id?: string }>} cards
 * @param {string | null | undefined} excludeCardId
 * @returns {number} index or -1 when no other card exists
 */
export function findRandomPracticeCookbookCardIndex(cards, excludeCardId) {
  if (!Array.isArray(cards) || cards.length <= 1) return -1;

  const eligible = [];
  for (let i = 0; i < cards.length; i += 1) {
    const card = cards[i];
    if (!card?.id || card.id === excludeCardId) continue;
    eligible.push(i);
  }

  if (eligible.length === 0) return -1;
  return eligible[Math.floor(Math.random() * eligible.length)];
}

/**
 * Next card in cookbook index order, wrapping to the start after the last card.
 * @param {Array<{ id?: string }>} cards
 * @param {number} currentIndex
 * @returns {number} index or -1 when only one card exists
 */
export function findNextIndexCookbookCardIndex(cards, currentIndex) {
  if (!Array.isArray(cards) || cards.length <= 1) return -1;

  const from = Number.isFinite(currentIndex) ? currentIndex : -1;
  if (from < 0) return 0;
  return (from + 1) % cards.length;
}

export function findNextEarliestReviewCookbookCardIndex(cards, excludeCardId, now = new Date()) {
  if (!Array.isArray(cards) || cards.length <= 1) return -1;

  let bestIndex = -1;
  let bestDueMs = Number.POSITIVE_INFINITY;

  for (let i = 0; i < cards.length; i += 1) {
    const card = cards[i];
    if (!card?.id || card.id === excludeCardId) continue;
    if (!isCookbookCardEligibleForAutoAdvance(card, now)) continue;

    const dueMs = resolveCookbookCardReviewDueAt(card, now).getTime();
    if (dueMs < bestDueMs) {
      bestDueMs = dueMs;
      bestIndex = i;
    }
  }

  return bestIndex;
}

/**
 * After finishing a snippet: prefer the next unstarted card, otherwise the
 * next card in cookbook order. Returns -1 when already on the last card.
 * @deprecated Prefer {@link findNextEarliestReviewCookbookCardIndex} after SRS completion.
 */
export function resolveNextCookbookCardIndexAfterComplete(cards, currentIndex) {
  if (!Array.isArray(cards) || cards.length === 0) return -1;

  const unstarted = findNextUnstartedCookbookCardIndex(cards, currentIndex);
  if (unstarted >= 0) return unstarted;

  const from = Number.isFinite(currentIndex) ? currentIndex : -1;
  if (from >= 0 && from < cards.length - 1) return from + 1;
  return -1;
}

/**
 * Build the stats shape expected by {@link isSnippetStarted} / {@link isSnippetMastered}.
 * @param {Record<string, unknown> | null | undefined} cardData
 * @param {Record<string, unknown> | null | undefined} progress
 */
export function statsFromCookbookCard(cardData, progress) {
  const p = progress && typeof progress === 'object' ? progress : null;
  const totalReviews = p && Number.isFinite(Number(p.totalReviews)) ? Number(p.totalReviews) : 0;
  const successfulReviews =
    p && Number.isFinite(Number(p.successfulReviews)) ? Number(p.successfulReviews) : 0;
  const successRate =
    totalReviews > 0 ? Math.round((successfulReviews / totalReviews) * 100) : 0;

  const rawLevel = cardData?.level;
  const level =
    typeof rawLevel === 'number' && Number.isFinite(rawLevel)
      ? Math.min(Math.max(Math.round(rawLevel), 1), MAX_SNIPPET_LEVEL)
      : 1;
  const rawExp = cardData?.current_exp;
  const current_exp =
    typeof rawExp === 'number' && Number.isFinite(rawExp) ? Math.max(0, Math.round(rawExp)) : 0;

  return {
    successRate,
    totalReviews,
    totalAttempts: totalReviews,
    level,
    current_exp,
    isMastered: snippetMasteredFromProgress(p),
    mastery: p ? Math.round(Math.min(100, Math.max(0, Number(p.mastery) || 0))) : 0,
    repetitions: p && Number.isFinite(Number(p.repetitions)) ? Number(p.repetitions) : 0,
    interval: p && Number.isFinite(Number(p.interval)) ? Number(p.interval) : 0,
    firstReviewed: p && typeof p.firstReviewed === 'string' ? p.firstReviewed : null,
    lastReviewed: p && typeof p.lastReviewed === 'string' ? p.lastReviewed : null
  };
}

export function isCookbookCardStartedNotDue(cardData, progress, now = new Date()) {
  const stats = statsFromCookbookCard(cardData, progress);
  if (!isSnippetStarted(stats)) return false;
  return !isCookbookSnippetDueToday(progress, now);
}

export function countCookbookProgressFromProgressMap(deckId, progressMap, now = new Date()) {
  let started = 0;
  let startedNotDue = 0;
  let mastered = 0;

  if (!deckId || !(progressMap instanceof Map)) {
    return { started, startedNotDue, mastered };
  }

  const prefix = `${deckId}_`;
  for (const [key, progress] of progressMap.entries()) {
    if (!key.startsWith(prefix)) continue;
    const stats = statsFromCookbookCard(null, progress);
    if (isSnippetStarted(stats)) {
      started += 1;
      if (isCookbookCardStartedNotDue(null, progress, now)) {
        startedNotDue += 1;
      }
    }
    if (isSnippetMastered(stats)) mastered += 1;
  }

  return { started, startedNotDue, mastered };
}

/**
 * @param {Array<{ id: string, data: () => Record<string, unknown> } | { id: string, data: Record<string, unknown> }>} cardDocs
 * @param {string} deckId
 * @param {Map<string, Record<string, unknown>>} progressMap
 * @returns {{ total: number, started: number, startedNotDue: number, mastered: number, averageSuccessRate: number }}
 */
export function countCookbookCardProgress(cardDocs, deckId, progressMap) {
  let total = 0;
  let started = 0;
  let startedNotDue = 0;
  let mastered = 0;
  let successSum = 0;
  let cardsWithReviews = 0;

  for (const cardDoc of cardDocs || []) {
    const cardData =
      typeof cardDoc.data === 'function' ? cardDoc.data() : cardDoc.data || cardDoc;
    if (!cardData?.solution || !String(cardData.solution).trim()) continue;

    total += 1;
    const progress = progressMap?.get(`${deckId}_${cardDoc.id}`) ?? null;
    const stats = statsFromCookbookCard(cardData, progress);
    if (isSnippetStarted(stats)) {
      started += 1;
      if (isCookbookCardStartedNotDue(cardData, progress)) {
        startedNotDue += 1;
      }
    }
    if (isSnippetMastered(stats)) mastered += 1;
    if (stats.totalReviews > 0) {
      successSum += stats.successRate;
      cardsWithReviews += 1;
    }
  }

  return {
    total,
    started,
    startedNotDue,
    mastered,
    averageSuccessRate: cardsWithReviews > 0 ? Math.round(successSum / cardsWithReviews) : 0
  };
}

/** True when every solution snippet in the cookbook is mastered. */
export function isCookbookFullyMastered({ total = 0, mastered = 0 } = {}) {
  const safeTotal = Number(total) || 0;
  const safeMastered = Number(mastered) || 0;
  return safeTotal > 0 && safeMastered === safeTotal;
}

/** Session cache for per-deck cookbook card stats (Home recents pool). */
const COOKBOOK_DECK_PROGRESS_TTL_MS = 60_000;
const cookbookDeckProgressCache = new Map();

/**
 * Load solution cards for a deck and compute started / mastered / total.
 * @param {string} userId
 * @param {string} deckId
 * @param {Map<string, Record<string, unknown>> | Array<{ id?: string, deckId?: string, cardId?: string }>} [progressMapOrRows]
 * @returns {Promise<{ total: number, started: number, mastered: number, averageSuccessRate: number }>}
 */
export async function loadCookbookCardProgress(userId, deckId, progressMapOrRows) {
  const empty = { total: 0, started: 0, startedNotDue: 0, mastered: 0, averageSuccessRate: 0 };
  if (!userId || !deckId) return empty;

  const cacheKey = `${userId}_${deckId}`;
  const cached = cookbookDeckProgressCache.get(cacheKey);
  if (cached && Date.now() - cached.at < COOKBOOK_DECK_PROGRESS_TTL_MS) {
    return cached.value;
  }

  try {
    const progressMap =
      progressMapOrRows instanceof Map
        ? progressMapOrRows
        : buildCardProgressMap(progressMapOrRows || []);

    const cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${deckId}/cards`));
    if (cardsSnap.empty) return empty;

    const value = countCookbookCardProgress(cardsSnap.docs, deckId, progressMap);
    cookbookDeckProgressCache.set(cacheKey, { at: Date.now(), value });
    return value;
  } catch (error) {
    console.error('loadCookbookCardProgress failed', deckId, error);
    return empty;
  }
}

/**
 * Load started cards for a library cookbook practice session.
 * @param {string} userId
 * @param {string} deckId
 * @param {Map<string, Record<string, unknown>> | Array<{ id?: string, deckId?: string, cardId?: string }>} [progressMapOrRows]
 * @returns {Promise<Array<{ id: string, deckId: string, progress: Record<string, unknown> | null } & Record<string, unknown>>>}
 */
export async function loadCookbookPracticeStartedCards(userId, deckId, progressMapOrRows) {
  if (!userId || !deckId) return [];

  try {
    const progressMap =
      progressMapOrRows instanceof Map
        ? progressMapOrRows
        : buildCardProgressMap(progressMapOrRows || []);

    const cardsSnap = await getDocs(collection(db, `users/${userId}/decks/${deckId}/cards`));
    if (cardsSnap.empty) return [];

    /** @type {Array<{ id: string, deckId: string, progress: Record<string, unknown> | null } & Record<string, unknown>>} */
    const cards = [];

    for (const cardDoc of cardsSnap.docs) {
      const cardData = cardDoc.data();
      if (!cardData?.solution || !String(cardData.solution).trim()) continue;

      const progress = progressMap.get(`${deckId}_${cardDoc.id}`) ?? null;
      if (!isSnippetStarted(statsFromCookbookCard(cardData, progress))) continue;

      cards.push({
        id: cardDoc.id,
        ...cardData,
        deckId,
        progress
      });
    }

    cards.sort((a, b) => {
      const cardCreatedAtMs = (card) => {
        const t = card?.createdAt;
        if (!t) return 0;
        if (typeof t.toMillis === 'function') return t.toMillis();
        if (typeof t.seconds === 'number') return t.seconds * 1000;
        if (t instanceof Date) return t.getTime();
        return 0;
      };
      const byCreated = cardCreatedAtMs(a) - cardCreatedAtMs(b);
      if (byCreated !== 0) return byCreated;
      return String(a.id).localeCompare(String(b.id));
    });

    return cards;
  } catch (error) {
    console.error('loadCookbookPracticeStartedCards failed', deckId, error);
    return [];
  }
}
